import { useEffect, useState } from "react";
import { Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useT } from "../lib/i18n";
import { money, todayStr } from "../lib/helpers";
import { PageHeader, Empty, Field } from "../components/Common";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

function exportCsv(items) {
  const cols = ["date", "location_name", "client_name", "service_name", "professional_name", "type", "method", "amount", "currency", "created_by"];
  const csv = [cols.join(","), ...items.map((p) => cols.map((c) => `"${String(p[c] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = `payments-${todayStr()}.csv`;
  a.click();
}

export default function Revenue() {
  const { locationId, can } = useApp();
  const { t, lang } = useT();
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());
  const [data, setData] = useState(null);

  const load = () => api.get("/payments", { params: { date_from: from, date_to: to, location_id: locationId || undefined } }).then((r) => setData(r.data));
  useEffect(() => { load(); }, [from, to, locationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const del = async (id) => {
    if (!window.confirm("Delete this payment?")) return;
    try { await api.delete(`/payments/${id}`); toast.success("Payment deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader eyebrow={t("cash_register")} title={t("revenue")}>
        <Field label={t("from")}><Input type="date" data-testid="revenue-from" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label={t("to")}><Input type="date" data-testid="revenue-to" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        <Button variant="outline" className="rounded-full self-end" data-testid="revenue-export-btn" disabled={!data?.items.length} onClick={() => exportCsv(data.items)}>
          <Download className="h-4 w-4 mr-1.5" />CSV
        </Button>
      </PageHeader>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-8 stagger" data-testid="revenue-totals">
        {data && !Object.keys(data.totals).length && <Empty text={t("no_data")} />}
        {data && Object.entries(data.totals).map(([cur, v]) => (
          <div key={cur} className="rounded-[var(--radius)] border bg-card p-6" data-testid={`revenue-total-${cur}`}>
            <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">{t("total")} · {cur} · {v.count}</p>
            <p className="font-display text-3xl font-bold mt-2">{money(v.total, cur, lang)}</p>
            <div className="mt-4 space-y-1 text-sm">
              {Object.entries(v.by_method).map(([m, a]) => (
                <div key={m} className="flex justify-between"><span className="capitalize text-muted-foreground">{m.replace("_", " ")}</span><span>{money(a, cur, lang)}</span></div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-[var(--radius)] border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="revenue-table">
          <thead className="text-xs uppercase tracking-wider text-muted-foreground border-b">
            <tr>{["date", "location", "client", "service", "professional", "method", "amount", ""].map((h) => <th key={h} className="text-left font-medium px-4 py-3">{h && t(h)}</th>)}</tr>
          </thead>
          <tbody className="divide-y">
            {data?.items.map((p) => (
              <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                <td className="px-4 py-3 font-mono text-xs">{p.date}</td><td className="px-4 py-3">{p.location_name}</td>
                <td className="px-4 py-3">{p.client_name}</td><td className="px-4 py-3">{p.service_name || p.type}</td>
                <td className="px-4 py-3">{p.professional_name}</td><td className="px-4 py-3 capitalize">{p.method.replace("_", " ")}</td>
                <td className="px-4 py-3 font-semibold">{money(p.amount, p.currency, lang)}</td>
                <td className="px-4 py-3">{can("payments.delete") && <button data-testid={`delete-payment-${p.id}`} onClick={() => del(p.id)}><Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
